import { useState, useEffect, useRef } from "react";
import { X, CheckCircle, Gift, PoundSterling, ArrowRight } from "lucide-react";
import { base44 } from "@/api/base44Client";
import { trackFormEvent } from "@/lib/formTracking";
import useAddressAutocomplete from "@/hooks/useAddressAutocomplete";

const FORM_NAME = "Referral Form";

const inputStyle = {
  width: "100%", padding: "12px 14px", border: "1.5px solid #E5E5E5", borderRadius: 10,
  fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#1A1A1A", background: "#fff",
  outline: "none", boxSizing: "border-box", transition: "border-color 0.2s",
};
const labelStyle = {
  fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, textTransform: "uppercase",
  letterSpacing: "0.08em", color: "#888", display: "block", marginBottom: 6, textAlign: "left",
};

export default function ReferHostModal({ onClose, onSubmitted }) {
  const hasSubmittedRef = useRef(false);
  const [referrer, setReferrer] = useState({ name: "", email: "", phone: "" });
  const [referee, setReferee] = useState({ name: "", email: "", phone: "", postcode: "", propertyType: "", bedrooms: "" });
  const [reward, setReward] = useState("cash");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const postcodeRef = useRef(null);
  useAddressAutocomplete(postcodeRef, (addr) => setReferee((p) => ({ ...p, postcode: addr })), { types: ["(regions)"] });

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const setR = (f) => (e) => { setReferrer(p => ({ ...p, [f]: e.target.value })); if (errors["r_" + f]) setErrors(prev => ({ ...prev, ["r_" + f]: undefined })); };
  const setE = (f) => (e) => { setReferee(p => ({ ...p, [f]: e.target.value })); if (errors["e_" + f]) setErrors(prev => ({ ...prev, ["e_" + f]: undefined })); };

  const validate = () => {
    const n = {};
    if (!referrer.name.trim()) n.r_name = "Required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(referrer.email)) n.r_email = "Valid email required";
    if (!referrer.phone.trim()) n.r_phone = "Required";
    if (!referee.name.trim()) n.e_name = "Required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(referee.email)) n.e_email = "Valid email required";
    if (!referee.phone.trim()) n.e_phone = "Required";
    if (!referee.postcode.trim()) n.e_postcode = "Required";
    setErrors(n);
    return Object.keys(n).length === 0;
  };

  const buildBody = () => [
    "=== REFERRAL SUBMISSION ===",
    "",
    "REFERRER (you):",
    `Name: ${referrer.name}`,
    `Email: ${referrer.email}`,
    `Phone: ${referrer.phone}`,
    "",
    "REFERRED HOST:",
    `Name: ${referee.name}`,
    `Email: ${referee.email}`,
    `Phone: ${referee.phone}`,
    `Postcode/area: ${referee.postcode}`,
    `Property type: ${referee.propertyType || "Not provided"}`,
    `Bedrooms: ${referee.bedrooms || "Not provided"}`,
    "",
    `Reward choice: ${reward === "cash" ? "£250 cash bonus" : "Up to 10% ongoing fee share"}`,
  ].join("\n");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    hasSubmittedRef.current = true;
    try { await base44.functions.invoke("sendLeadEmail", { subject: `New referral from ${referrer.name}`, body: buildBody() }); } catch (_) {}
    try {
      await base44.functions.invoke("saveLeadToSheet", {
        formData: {
          fullName: referrer.name,
          email: referrer.email,
          phone: referrer.phone,
          type: "referral",
          refereeName: referee.name,
          refereeEmail: referee.email,
          refereePhone: referee.phone,
          postcode: referee.postcode,
          propertyType: referee.propertyType,
          bedrooms: referee.bedrooms,
          reward: reward === "cash" ? "£250 cash bonus" : "Up to 10% ongoing fee share",
        },
        uploadedFiles: [],
      });
    } catch (_) {}
    if (referrer.email) {
      try {
        await base44.functions.invoke("sendConfirmationEmail", {
          to: referrer.email,
          name: referrer.name,
          enquiryType: "referral",
          fields: [
            { label: "Your name", value: referrer.name },
            { label: "Your email", value: referrer.email },
            { label: "Your phone", value: referrer.phone },
            { label: "Referred host name", value: referee.name },
            { label: "Referred host email", value: referee.email },
            { label: "Referred host phone", value: referee.phone },
            { label: "Referred host area", value: referee.postcode },
            { label: "Property type", value: referee.propertyType || "Not provided" },
            { label: "Bedrooms", value: referee.bedrooms || "Not provided" },
            { label: "Reward choice", value: reward === "cash" ? "£250 cash bonus" : "Up to 10% ongoing fee share" },
          ],
        });
      } catch (_) {}
    }
    trackFormEvent(FORM_NAME, true);
    setSubmitting(false);
    setSubmitted(true);
    setTimeout(() => onSubmitted(), 1800);
  };

  const handleClose = () => {
    if (!hasSubmittedRef.current) trackFormEvent(FORM_NAME, false);
    onClose();
  };

  const errStyle = { color: "#C4C024", fontSize: 11, marginTop: 4, fontFamily: "'DM Sans', sans-serif" };

  return (
    <div onClick={e => { if (e.target === e.currentTarget) handleClose(); }} style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ width: "100%", maxWidth: 560, background: "#fff", borderRadius: 16, boxShadow: "0 24px 80px rgba(0,0,0,0.3)", position: "relative", maxHeight: "90vh", overflowY: "auto" }} className="refer-modal">
        <button onClick={handleClose} style={{ position: "absolute", top: 14, right: 14, background: "#F5F5F5", border: "none", borderRadius: "50%", width: 32, height: 32, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10 }}>
          <X size={16} color="#888" />
        </button>

        {submitted ? (
          <div style={{ textAlign: "center", padding: "56px 32px" }}>
            <CheckCircle size={44} color="#1A1A1A" style={{ margin: "0 auto 16px" }} />
            <h3 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 22, color: "#1A1A1A", margin: "0 0 8px" }}>Referral sent!</h3>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: "#888", margin: 0 }}>Thanks for the introduction. We'll reach out to them and be in touch with you about your reward.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ padding: "40px 32px" }}>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "#1A1A1A", margin: "0 0 6px" }}>Refer a host</p>
            <h3 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 22, color: "#1A1A1A", margin: "0 0 6px" }}>Introduce a landlord</h3>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#888", margin: "0 0 24px", lineHeight: 1.55 }}>Tell us about you and the person you're referring. We'll handle the rest.</p>

            <p style={{ ...labelStyle, marginBottom: 10, color: "#1A1A1A" }}>Your details</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }} className="refer-modal-grid">
              <div>
                <label style={labelStyle}>Full name *</label>
                <input style={{ ...inputStyle, borderColor: errors.r_name ? "#C4C024" : "#E5E5E5" }} value={referrer.name} onChange={setR("name")} placeholder="Your name" />
                {errors.r_name && <p style={errStyle}>{errors.r_name}</p>}
              </div>
              <div>
                <label style={labelStyle}>Phone *</label>
                <input style={{ ...inputStyle, borderColor: errors.r_phone ? "#C4C024" : "#E5E5E5" }} value={referrer.phone} onChange={setR("phone")} placeholder="07XXX XXXXXX" />
                {errors.r_phone && <p style={errStyle}>{errors.r_phone}</p>}
              </div>
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Email *</label>
              <input type="email" style={{ ...inputStyle, borderColor: errors.r_email ? "#C4C024" : "#E5E5E5" }} value={referrer.email} onChange={setR("email")} placeholder="you@example.com" />
              {errors.r_email && <p style={errStyle}>{errors.r_email}</p>}
            </div>

            <div style={{ height: 1, background: "#EBEBEB", margin: "0 0 20px" }} />

            <p style={{ ...labelStyle, marginBottom: 10, color: "#1A1A1A" }}>Their details</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }} className="refer-modal-grid">
              <div>
                <label style={labelStyle}>Full name *</label>
                <input style={{ ...inputStyle, borderColor: errors.e_name ? "#C4C024" : "#E5E5E5" }} value={referee.name} onChange={setE("name")} placeholder="Their name" />
                {errors.e_name && <p style={errStyle}>{errors.e_name}</p>}
              </div>
              <div>
                <label style={labelStyle}>Phone *</label>
                <input style={{ ...inputStyle, borderColor: errors.e_phone ? "#C4C024" : "#E5E5E5" }} value={referee.phone} onChange={setE("phone")} placeholder="07XXX XXXXXX" />
                {errors.e_phone && <p style={errStyle}>{errors.e_phone}</p>}
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Email *</label>
              <input type="email" style={{ ...inputStyle, borderColor: errors.e_email ? "#C4C024" : "#E5E5E5" }} value={referee.email} onChange={setE("email")} placeholder="they@example.com" />
              {errors.e_email && <p style={errStyle}>{errors.e_email}</p>}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }} className="refer-modal-grid">
              <div>
                <label style={labelStyle}>Postcode / area *</label>
                <input ref={postcodeRef} style={{ ...inputStyle, borderColor: errors.e_postcode ? "#C4C024" : "#E5E5E5" }} value={referee.postcode} onChange={setE("postcode")} placeholder="e.g. M1 1AA" autoComplete="off" />
                {errors.e_postcode && <p style={errStyle}>{errors.e_postcode}</p>}
              </div>
              <div>
                <label style={labelStyle}>Bedrooms</label>
                <select style={inputStyle} value={referee.bedrooms} onChange={setE("bedrooms")}>
                  <option value="">Select…</option>
                  <option>Studio</option><option>1 bed</option><option>2 beds</option><option>3 beds</option><option>4+ beds</option>
                </select>
              </div>
            </div>

            <p style={{ ...labelStyle, marginBottom: 10, color: "#1A1A1A" }}>Your reward</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 24 }} className="refer-modal-grid">
              {[
                { key: "cash", icon: PoundSterling, title: "£250 cash", desc: "One-off, after 8th booking" },
                { key: "share", icon: Gift, title: "Up to 10% fees", desc: "Recurring, paid quarterly" },
              ].map((opt) => {
                const active = reward === opt.key;
                const Icon = opt.icon;
                return (
                  <button type="button" key={opt.key} onClick={() => setReward(opt.key)} style={{ textAlign: "left", background: active ? "#1A1A1A" : "#fff", border: "1.5px solid " + (active ? "#1A1A1A" : "#E5E5E5"), borderRadius: 12, padding: "14px 16px", cursor: "pointer", display: "flex", alignItems: "center", gap: 10, transition: "all 0.15s" }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: active ? "rgba(255,255,255,0.12)" : "#F0F0F0", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <Icon size={15} color={active ? "#fff" : "#1A1A1A"} />
                    </div>
                    <div>
                      <p style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: 13, color: active ? "#fff" : "#1A1A1A", margin: 0 }}>{opt.title}</p>
                      <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: active ? "rgba(255,255,255,0.6)" : "#888", margin: "2px 0 0" }}>{opt.desc}</p>
                    </div>
                  </button>
                );
              })}
            </div>

            <button type="submit" disabled={submitting} style={{ width: "100%", background: "#112215", color: "#fff", border: "none", borderRadius: 100, padding: "15px", fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, transition: "background 0.2s" }}
              onMouseEnter={e => e.currentTarget.style.background = "#000"}
              onMouseLeave={e => e.currentTarget.style.background = "#112215"}>
              {submitting ? "Sending…" : <>Submit referral <ArrowRight size={16} /></>}
            </button>
          </form>
        )}
        <style>{`@media (max-width: 560px) { .refer-modal-grid { grid-template-columns: 1fr !important; } .refer-modal { border-radius: 0 !important; max-height: 100dvh !important; } }`}</style>
      </div>
    </div>
  );
}